import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

export type CardTone = 'default' | 'urgent' | 'routine' | 'sunken';
export type CardElement = 'article' | 'section' | 'div';

export interface CardProps {
  tone?: CardTone;
  as?: CardElement;
  className?: string;
  children: ReactNode;
  'aria-label'?: string;
}

interface CardSlotProps {
  className?: string;
  children: ReactNode;
}

const TONE_CLASSES: Readonly<Record<CardTone, string>> = {
  default: 'border-border bg-card',
  urgent: 'border-urgent-outline bg-urgent-surface',
  routine: 'border-routine-outline bg-routine-surface',
  sunken: 'border-transparent bg-surface-sunken',
};

const CARD_BASE_CLASS = 'flex flex-col gap-2 rounded-[12px] border p-3';

function CardRoot({
  tone = 'default',
  as = 'article',
  className,
  children,
  'aria-label': ariaLabel,
}: CardProps) {
  const classes = cn(CARD_BASE_CLASS, TONE_CLASSES[tone], className);
  switch (as) {
    case 'section':
      return (
        <section aria-label={ariaLabel} className={classes}>
          {children}
        </section>
      );
    case 'div':
      return (
        <div aria-label={ariaLabel} className={classes}>
          {children}
        </div>
      );
    case 'article':
    default:
      return (
        <article aria-label={ariaLabel} className={classes}>
          {children}
        </article>
      );
  }
}

function CardHeader({ className, children }: CardSlotProps) {
  return <div className={cn('flex items-center gap-2', className)}>{children}</div>;
}

function CardBody({ className, children }: CardSlotProps) {
  return <div className={cn('flex flex-col gap-2', className)}>{children}</div>;
}

function CardFooter({ className, children }: CardSlotProps) {
  return <div className={cn('flex items-center gap-2', className)}>{children}</div>;
}

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});
